import React, { useState, useCallback } from 'react';
import { FaImages, FaCloudUploadAlt, FaTrash, FaCheck, FaTimes, FaLink } from 'react-icons/fa';

const SellerImageUpload = () => {
    const [images, setImages] = useState([]);
    const [urlInput, setUrlInput] = useState('');
    const [uploadMethod, setUploadMethod] = useState('file'); // file, url

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        handleFiles(files);
    }, []);

    const handleFiles = (files) => {
        const newImages = files.map((file, index) => ({
            id: Date.now() + index,
            name: file.name,
            size: (file.size / 1024).toFixed(2) + ' KB',
            preview: URL.createObjectURL(file),
            status: 'pending',
            sku: '',
        }));
        setImages(prev => [...prev, ...newImages]);
    };

    const handleAddUrl = () => {
        if (!urlInput.trim()) return;
        const newImage = {
            id: Date.now(),
            name: urlInput.split('/').pop() || 'image.jpg',
            preview: urlInput,
            status: 'pending',
            sku: '',
            isUrl: true,
        };
        setImages(prev => [...prev, newImage]);
        setUrlInput('');
    };

    const removeImage = (id) => {
        setImages(prev => prev.filter(img => img.id !== id));
    };

    const updateSku = (id, sku) => {
        setImages(prev => prev.map(img => img.id === id ? { ...img, sku } : img));
    };

    const handleUploadAll = () => {
        setImages(prev => prev.map(img => ({ ...img, status: 'success' })));
        alert('All images uploaded successfully!');
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Image Bulk Upload</h1>
                <p className="text-gray-600">Upload multiple product images at once</p>
            </div>

            {/* Upload Method Toggle */}
            <div className="flex gap-4 mb-6">
                <button
                    onClick={() => setUploadMethod('file')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${uploadMethod === 'file' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                >
                    <FaCloudUploadAlt /> Upload Files
                </button>
                <button
                    onClick={() => setUploadMethod('url')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${uploadMethod === 'url' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                >
                    <FaLink /> From URL
                </button>
            </div>

            {/* File Upload Area */}
            {uploadMethod === 'file' && (
                <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="bg-white rounded-lg shadow p-8 mb-6 border-2 border-dashed border-gray-300 hover:border-blue-500 transition text-center"
                >
                    <label className="cursor-pointer">
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handleFiles(Array.from(e.target.files))}
                            className="hidden"
                        />
                        <FaImages className="text-6xl text-gray-400 mx-auto mb-4" />
                        <p className="text-lg font-semibold text-gray-700">Drag & drop images here</p>
                        <p className="text-sm text-gray-500 mt-2">or click to browse files</p>
                        <p className="text-xs text-gray-400 mt-4">Supports: JPG, PNG, WebP (Max 5MB each)</p>
                    </label>
                </div>
            )}

            {/* URL Input */}
            {uploadMethod === 'url' && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <div className="flex gap-4">
                        <input
                            type="url"
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            placeholder="Enter image URL (https://...)"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleAddUrl}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Add Image
                        </button>
                    </div>
                </div>
            )}

            {/* Image List */}
            {images.length > 0 && (
                <>
                    <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
                        <div className="px-6 py-4 border-b flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-800">Images ({images.length})</h2>
                            <button
                                onClick={handleUploadAll}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Upload All
                            </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-6">
                            {images.map((image) => (
                                <div key={image.id} className="relative group">
                                    <img
                                        src={image.preview}
                                        alt={image.name}
                                        className="w-full h-32 object-cover rounded-lg border"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                                    />
                                    <button
                                        onClick={() => removeImage(image.id)}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                                    >
                                        <FaTrash size={12} />
                                    </button>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            placeholder="SKU"
                                            value={image.sku}
                                            onChange={(e) => updateSku(image.id, e.target.value)}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                        />
                                    </div>
                                    {image.status === 'success' && (
                                        <div className="absolute bottom-12 left-2">
                                            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                                                <FaCheck size={10} /> Done
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Instructions */}
            <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Tips for Best Results</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Use high-quality images (minimum 800x800 pixels)</li>
                    <li>• White or neutral background recommended</li>
                    <li>• Enter SKU to automatically link images to products</li>
                    <li>• First image will be set as the main product image</li>
                </ul>
            </div>
        </div>
    );
};

export default SellerImageUpload;
